import { useState } from "react";
import {
  Bot,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Languages,
  ShieldAlert,
  ShieldCheck,
  User,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ConfidenceBar } from "@/components/confidence-bar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatTimestamp, xPostUrl } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { ClassifiedPost } from "@/lib/types";

export function PostCard({ post }: { post: ClassifiedPost }) {
  const [open, setOpen] = useState(false);
  const isAntisem = post.label === "antisemitic";
  const isNotAntisem = post.label === "not_antisemitic";

  return (
    <Card
      className={cn(
        "relative overflow-hidden border-border/60",
        isAntisem && "border-destructive/30 bg-destructive/5"
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-0 left-0 w-0.5",
          isAntisem
            ? "bg-destructive/70"
            : isNotAntisem
              ? "bg-[color:var(--color-success)]/60"
              : "bg-border"
        )}
      />
      <CardContent className="flex flex-col gap-3 p-5 pl-6">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {isAntisem ? (
            <Badge
              variant="destructive"
              className="gap-1.5 border-destructive/30 px-2 py-0.5"
            >
              <ShieldAlert className="h-3 w-3" />
              Antisemitic
            </Badge>
          ) : isNotAntisem ? (
            <Badge variant="success" className="gap-1.5 px-2 py-0.5">
              <ShieldCheck className="h-3 w-3" />
              Not antisemitic
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1.5 px-2 py-0.5">
              Unclassified
            </Badge>
          )}
          <ConfidenceBar
            value={post.confidence}
            tone={
              isAntisem
                ? "antisemitic"
                : isNotAntisem
                  ? "not_antisemitic"
                  : "neutral"
            }
          />
          <div className="ml-auto flex items-center gap-3 text-[11px]">
            {post.model_name ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1">
                    <Bot className="h-3 w-3" />
                    <span className="font-mono">
                      {shortModelName(post.model_name)}
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Classified by {post.model_name}
                </TooltipContent>
              </Tooltip>
            ) : null}
            {post.lang ? (
              <span className="inline-flex items-center gap-1">
                <Languages className="h-3 w-3" />
                {post.lang}
              </span>
            ) : null}
            {post.author_id ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span className="font-mono">
                      {truncateMiddle(post.author_id, 10)}
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Author ID: {post.author_id}</TooltipContent>
              </Tooltip>
            ) : null}
            {post.created_at ? (
              <span>{formatTimestamp(post.created_at)}</span>
            ) : null}
          </div>
        </div>

        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
          {post.text}
        </p>

        {post.reason ? (
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 self-start text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {open ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                {open ? "Hide reasoning" : "Show reasoning"}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 rounded-md border border-border/60 bg-secondary/40 p-3 text-xs text-muted-foreground">
              <p className="whitespace-pre-wrap leading-relaxed">
                {post.reason}
              </p>
              {post.model_name || post.prompt_version ? (
                <div className="mt-2 flex flex-wrap gap-3 border-t border-border/40 pt-2 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                  {post.model_name ? <span>model: {post.model_name}</span> : null}
                  {post.prompt_version ? (
                    <span>prompt: {post.prompt_version}</span>
                  ) : null}
                </div>
              ) : null}
            </CollapsibleContent>
          </Collapsible>
        ) : null}

        <div className="flex items-center justify-between border-t border-border/40 pt-3 text-[11px] text-muted-foreground">
          <span className="font-mono">{post.x_post_id}</span>
          <a
            href={xPostUrl(post.x_post_id)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            View on X
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

function shortModelName(value: string): string {
  const slug = value.split("/").pop() ?? value;
  return slug.length <= 24 ? slug : `${slug.slice(0, 23)}…`;
}

function truncateMiddle(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  const half = Math.floor((maxLength - 1) / 2);
  return `${value.slice(0, half)}…${value.slice(value.length - half)}`;
}
